import { type Vec2 } from '@d2/core';

import { STAIRS_BEACON_INSET } from './art-spec';
import { type Viewport } from './camera';

/**
 * Where to point when the thing a player is looking for is off screen.
 *
 * `docs/GAME-DESIGN.md` places the stairs *"at maximum flow-field distance"*
 * from the entrance, and says why: *"it guarantees every floor is a real
 * traversal rather than three steps to the exit."* On a 128×128 floor that is a
 * hundred and eighty cells at the extreme, and the camera shows about twenty
 * across — so for the whole of a run the way down is a sprite nobody can see.
 * Drawing it on its cell and stopping there would make the exit findable only by
 * sweeping the map, which is not a traversal, it is a search.
 *
 * So there is a second mark: an arrow pinned to the edge of the view, pointing
 * the way. It is the standard off-screen indicator and it is the cheapest thing
 * that makes the design's own sentence true — one marker, one bearing, no
 * minimap, no map memory, and nothing revealed that walking would not reveal.
 * The arrow says *which way*, never *how far* or *what is between here and
 * there*: the fight for the ground in between is the run.
 *
 * ## It exits along the ray, and is not clamped per axis
 *
 * The marker is placed where the straight line from the middle of the view to
 * the target crosses the inset frame — not by clamping each axis independently.
 * The two agree on a target straight up or straight out and disagree at every
 * corner, and the corner is where it matters: a per-axis clamp puts the arrow at
 * the corner itself whatever the bearing, so an arrow drawn there points
 * somewhere the stairs are not. Exiting along the ray keeps the marker's
 * position and its bearing the same fact stated twice.
 *
 * ## Everything here is world pixels
 *
 * `camera.ts`'s reason, restated: the question is about a **screen**, so cells
 * are the wrong unit for it, and `./iso` has already turned the cell into a
 * point. A caller with a zoomed camera divides by the zoom on the way in and
 * nothing here needs to know that it did.
 *
 * Pure and Phaser-free, so the geometry is unit-testable without a browser —
 * which for a marker that is only ever drawn where the map is *not* is the only
 * way it can be checked at all.
 */

/** A camera, as this module needs it: where it looks and how much it sees. */
export interface BeaconView {
  /** The middle of the view, in world pixels. */
  readonly centre: Vec2;
  /** How much world is visible — `camera.width / zoom` by `camera.height / zoom`. */
  readonly viewport: Viewport;
}

/** An off-screen thing, as a mark on the edge of the view. */
export interface Beacon {
  /** Where to draw the marker, in world pixels: on the inset frame, along the line to the target. */
  readonly position: Vec2;
  /**
   * Which way the target lies, in **degrees clockwise from screen right** —
   * Phaser's own convention for `setAngle`.
   *
   * Screen `y` grows downward in this projection, so a target below the camera
   * is a positive angle. A marker drawn with its point at the right-hand edge of
   * its texture therefore aims at the target after exactly one `setAngle`.
   */
  readonly angle: number;
}

const DEGREES_PER_RADIAN = 180 / Math.PI;

/**
 * The marker for something off screen, or `null` while it is on screen and can
 * speak for itself.
 *
 * `null` rather than a marker at the target's own position, because the two are
 * different marks with different jobs: on screen the *stairwell* says where the
 * stairs are, and an arrow over it would be a second mark saying the same thing
 * — worse, one drawn at a screen-space size on top of a world-space sprite.
 *
 * The inset is half the marker's own diagonal by default (`art-spec.ts`), so the
 * arrow is wholly drawn at every bearing rather than clipped by the edge it is
 * pinned to.
 */
export const edgeBeacon = (target: Vec2, { centre, viewport }: BeaconView, inset = STAIRS_BEACON_INSET): Beacon | null => {
  // Floored at zero for `peekLimit`'s reason: a window smaller than the marker
  // pins it to the middle rather than inverting the frame into a rectangle
  // nothing can satisfy.
  const half = { x: Math.max(0, viewport.width / 2 - inset), y: Math.max(0, viewport.height / 2 - inset) };
  const away = { x: target.x - centre.x, y: target.y - centre.y };

  if (Math.abs(away.x) <= half.x && Math.abs(away.y) <= half.y) return null;

  // How far along the ray the frame is crossed: the nearer of the two axis
  // crossings, which is the one the ray actually leaves through. An axis the ray
  // does not move along cannot be the one it exits by, hence the infinities.
  const reach = Math.min(
    away.x === 0 ? Number.POSITIVE_INFINITY : half.x / Math.abs(away.x),
    away.y === 0 ? Number.POSITIVE_INFINITY : half.y / Math.abs(away.y),
  );

  return {
    position: { x: centre.x + away.x * reach, y: centre.y + away.y * reach },
    angle: Math.atan2(away.y, away.x) * DEGREES_PER_RADIAN,
  };
};
